// Lightweight browser-side AI Layer utilities for runtime context
// - Provides safe helpers for capturing app state, screenshots (best-effort),
//   and hooks into console.* so app console output can be forwarded to the AI log store.
// - Intentionally small and careful (no heavy deps) so it works with small models and limited environments.

const layer = {
  initialized: false,
  store: null,

  init(store) {
    if (this.initialized) return this;
    this.initialized = true;
    this.store = store || (typeof window !== 'undefined' ? window.$store : null);

    // Install console hook so app console logs are mirrored to the terminal debug logs
    this._installConsoleHook();

    // convenience bridge to create an ai-chat conversation on demand
    this.createConversation = async (opts = { type: 'ai-chat' }) => {
      try {
        if (!this.store || !this.store.dispatch) return null
        return await this.store.dispatch('agent/createConversation', opts, { root: true })
      } catch (e) {
        console.warn('[agent-layer] createConversation failed', e && e.message)
        return null
      }
    }

    // Post an AI-authored message into a conversation (userId = agentUserId)
    this.postAIMessage = async (conversationId, text) => {
      try {
        if (!this.store || !this.store.dispatch) return false
        await this.store.dispatch('update', { type: 'msgs', mId: conversationId, text: text, userId: (this.store.state && this.store.state.agentUserId) || null }, { root: true })
        return true
      } catch (e) {
        console.warn('[agent-layer] postAIMessage failed', e && e.message)
        return false
      }
    }

    return this;
  },

  // primary forwarding function - send a structured xmbl log via the store action
  async sendLog(logMeta = {}) {
    if (!this.store) return false;
    try {
      // Prefer namespaced agent action if available
      if (this.store.dispatch) {
        await this.store.dispatch('agent/xmblLog', logMeta);
        return true;
      }
    } catch (e) {
      // swallow errors - logging shouldn't break app
      console.warn('[agent-layer] sendLog failed', e.message);
    }
    return false;
  },

  // Capture an app 'screenshot' (best-effort). If html2canvas is available it will use that.
  // Otherwise it returns a small html snapshot (base64'd) and metadata.
  async takeScreenshot(opts = { useHtml2canvas: true, clip: null }) {
    // Try html2canvas if present
    if (typeof window === 'undefined') return null;

    const out = { timestamp: Date.now(), kind: 'screenshot', data: null, meta: {} };

    try {
      if (opts.useHtml2canvas && window.html2canvas) {
        const canvas = await window.html2canvas(document.body, { logging: false });
        out.data = canvas.toDataURL('image/png');
        out.meta.width = canvas.width;
        out.meta.height = canvas.height;
        return out;
      }

      // Fallback: use serialized DOM trimmed to a limited size
      let html = document.documentElement ? document.documentElement.outerHTML : document.body.outerHTML;
      // small safety size
      if (html && html.length > 20000) html = html.slice(0, 20000) + '\n...[truncated]';
      out.data = 'html_snapshot:' + btoa(unescape(encodeURIComponent(html || '')));
      out.meta.truncated = html && html.length > 20000;
      out.meta.url = window.location.href;
      out.meta.userAgent = navigator.userAgent;
      out.meta.width = window.innerWidth;
      out.meta.height = window.innerHeight;
      return out;
    } catch (e) {
      out.data = null;
      out.meta.error = e.message;
      return out;
    }
  },

  // Query the app state. Accepts optional paths array of dot-paths to include.
  // If no paths provided, a shallow copy of the full store.state is returned (careful).
  captureState(paths = []) {
    if (!this.store) return {};

    try {
      const state = this.store.state || {};
      // If paths provided pick only those keys
      if (Array.isArray(paths) && paths.length > 0) {
        const out = {};
        paths.forEach(p => {
          try {
            const parts = p.split('.');
            let node = state;
            for (let part of parts) {
              if (node && Object.prototype.hasOwnProperty.call(node, part)) node = node[part];
              else {
                node = undefined; break;
              }
            }
            out[p] = node === undefined ? null : JSON.parse(JSON.stringify(node));
          } catch (e) {
            out[p] = null;
          }
        });
        return out;
      }

      // No paths: return a shallow, safe serialization of store.state (avoid functions)
      try {
        const safe = JSON.parse(JSON.stringify(state));
        return safe;
      } catch (e) {
        // give a minimal state snapshot
        const partial = { keys: Object.keys(state).slice(0, 30), _note: 'serializing failed' };
        return partial;
      }
    } catch (e) {
      return { error: e.message };
    }
  },

  // Discover what UI elements are present on the current view to help the agent scope its actions
  // Returns a list of interactive types (links with edit, inputs, textareas, buttons) with limited metadata
  discoverViewScope() {
    if (typeof document === 'undefined') return {};

    const elements = [];
    const inputs = Array.from(document.querySelectorAll('input,textarea,select,button,a,[contenteditable]'));
    inputs.slice(0, 200).forEach(el => {
      const tag = el.tagName.toLowerCase();
      const meta = {
        tag,
        type: el.getAttribute('type') || null,
        name: el.getAttribute('name') || null,
        id: el.id || null,
        classes: el.className || null,
        visible: !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length),
        value: (tag === 'input' || tag === 'textarea' || tag === 'select') ? (el.value || '') : undefined
      };
      elements.push(meta);
    });

    return { timestamp: Date.now(), url: window.location.href, count: elements.length, elements };
  },

  // Attach hooks so when console.* is called we forward logs to the agent layer
  _installConsoleHook() {
    if (typeof window === 'undefined' || !window.console || this._consoleHookInstalled) return;
    this._consoleHookInstalled = true;

    const origLog = console.log.bind(console);
    const origWarn = console.warn.bind(console);
    const origErr = console.error.bind(console);

    const forward = (level, args) => {
      try {
        const summary = (args && args.length && typeof args[0] === 'string') ? args[0] : (args && args.length ? JSON.stringify(args).slice(0, 1024) : 'console');
        const data = { level, args: args.slice(0, 10) };
        // Best-effort: async send, do not await
        setTimeout(() => { this.sendLog({ type: 'console', level, summary, data, timestamp: Date.now() }); }, 0);
      } catch (e) {
        // ignore
      }
    };

    console.log = (...args) => { origLog(...args); forward('log', args); };
    console.warn = (...args) => { origWarn(...args); forward('warn', args); };
    console.error = (...args) => { origErr(...args); forward('error', args); };

    return true;
  },

  // Recursive generator function for AI feedback loops
  // Keeps updating state between activities, executes actions that user may approve of,
  // and provides context for AI feedback loops
  async *feedbackLoopGenerator(initialContext = {}) {
    if (!this.store) {
      throw new Error('Agent layer not initialized')
    }

    let context = { ...initialContext, attempts: 0, maxAttempts: 10 }
    let shouldStop = false

    // Set stop handler
    this._stopFeedbackLoop = () => {
      shouldStop = true
    }

    while (context.attempts < context.maxAttempts && !shouldStop) {
      context.attempts++
      
      // Update state to indicate feedback loop is active
      this.store.commit('set', {
        keys: ['terminal', 'lastPlan', 'feedbackLoop'],
        val: true
      }, { root: true })

      try {
        // Get current introspection
        const introspection = this.store.state.agent?.introspection || {}
        
        // Get current context from terminal
        const currentContext = Array.isArray(this.store.state.terminal.context) 
          ? this.store.state.terminal.context.slice() 
          : []

        // Add feedback context
        const feedbackContext = {
          attempt: context.attempts,
          previousErrors: context.errors || [],
          introspection: {
            components: Object.keys(introspection.components || {}),
            functions: Object.keys(introspection.functions || {}),
            actions: introspection.dispatchActions?.map(a => a.name) || []
          }
        }

        // Call AI with feedback context
        const aiResponse = await this.store.dispatch('agent/discoverAndAssembleComponents', {
          userPrompt: context.userPrompt || this.store.state.terminal.inputs[this.store.state.terminal.currentConversationId] || '',
          contextData: context.contextData || {},
          introspection: introspection,
          currentContext: [...currentContext, { type: 'feedback', value: feedbackContext, timestamp: Date.now() }]
        })

        // Parse response
        let parsedResponse
        try {
          parsedResponse = typeof aiResponse === 'string' ? JSON.parse(aiResponse) : aiResponse
        } catch (e) {
          // Try to extract JSON
          const jsonMatch = aiResponse.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/) || aiResponse.match(/\{[\s\S]*\}/)
          if (jsonMatch) {
            parsedResponse = JSON.parse(jsonMatch[1] || jsonMatch[0])
          } else {
            throw new Error('Failed to parse AI response')
          }
        }

        // Check for validation errors
        const validation = await this.store.dispatch('agent/validateRecommendations', {
          components: parsedResponse.recommended_components || [],
          functions: parsedResponse.recommended_functions || [],
          actions: parsedResponse.recommended_actions || [],
          introspection: introspection
        }, { root: true })

        if (validation && !validation.isValid && validation.errors && validation.errors.length > 0) {
          // Add errors to context for next iteration
          context.errors = (context.errors || []).concat(validation.errors)
          context.lastError = validation.errors[0]
          
          // Yield error state
          yield {
            type: 'error',
            attempt: context.attempts,
            errors: validation.errors,
            response: parsedResponse,
            context: feedbackContext
          }

          // Continue loop to retry
          continue
        }

        // Success - yield result
        yield {
          type: 'success',
          attempt: context.attempts,
          response: parsedResponse,
          context: feedbackContext
        }

        // Update context with successful response
        context.lastSuccess = parsedResponse
        break

      } catch (error) {
        // Add error to context
        context.errors = (context.errors || []).concat([error.message])
        context.lastError = error.message

        // Yield error state
        yield {
          type: 'error',
          attempt: context.attempts,
          errors: [error.message],
          context: context
        }

        // If max attempts reached, break
        if (context.attempts >= context.maxAttempts) {
          break
        }
      }
    }

    // Clear feedback loop flag
    this.store.commit('set', {
      keys: ['terminal', 'lastPlan', 'feedbackLoop'],
      val: false
    }, { root: true })

    // Collect final context and send one-shot if stopped
    if (shouldStop) {
      const finalContext = {
        stopped: true,
        attempts: context.attempts,
        errors: context.errors || [],
        lastSuccess: context.lastSuccess || null
      }

      // Send final one-shot to AI
      try {
        await this.store.dispatch('agent/callAI', {
          prompt: `Feedback loop stopped by user. Final context: ${JSON.stringify(finalContext, null, 2)}`
        })
      } catch (e) {
        console.warn('[agent-layer] Failed to send final one-shot', e)
      }
    }

    return context
  },

  // Start feedback loop
  async startFeedbackLoop(initialContext = {}) {
    if (!this.store) {
      throw new Error('Agent layer not initialized')
    }

    const generator = this.feedbackLoopGenerator(initialContext)
    const results = []

    try {
      for await (const result of generator) {
        results.push(result)
        
        // Update store with current result
        this.store.commit('set', {
          keys: ['terminal', 'lastPlan', 'feedbackResult'],
          val: result
        }, { root: true })

        // If success, break early
        if (result.type === 'success') {
          break
        }
      }
    } catch (error) {
      console.error('[agent-layer] Feedback loop error', error)
    }

    return results
  },

  // Stop feedback loop
  async stopFeedbackLoop() {
    if (this._stopFeedbackLoop) {
      this._stopFeedbackLoop()
      this._stopFeedbackLoop = null
    }
  }
};

export default layer;
